import React from "react";
import type { Box } from "./frame";
import type { Theme } from "./theme";
import { iconForLabel, Icon } from "./icons";

/**
 * Anivaze v4 — 3D ISOMETRIC PIPELINE actor. A different ANIMATION TYPE (not more
 * flat circles-and-lines): labeled 3D cuboid "boxes" on a ground plane, wired in
 * a pipeline, with glowing packets flowing stage→stage in isometric 3D. Gives
 * flow/process beats a fresh, dimensional look so videos aren't monotonous.
 *
 * Pure function of `t`, deterministic. Director drives it via items = the stage
 * labels (e.g. ["Raw Data","Model","Prediction"]).
 */

const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);
const COS = Math.cos(Math.PI / 6), SIN = Math.sin(Math.PI / 6); // 30° isometric

interface VProps { items: string[]; box: Box; theme: Theme; unit: number; t: number }

type P2 = { x: number; y: number };
const shade = (hex: string, f: number): string => {
  const n = parseInt(hex.replace("#", ""), 16);
  const r = Math.round(Math.min(255, Math.max(0, ((n >> 16) & 255) * f)));
  const g = Math.round(Math.min(255, Math.max(0, ((n >> 8) & 255) * f)));
  const b = Math.round(Math.min(255, Math.max(0, (n & 255) * f)));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
};

export const IsoPipelineActor: React.FC<VProps> = ({ items, box, theme, t }) => {
  const P = theme.palette;
  const labels = items.map((s) => (s ?? "").trim()).filter(Boolean).slice(0, 4);
  if (labels.length === 0) labels.push("Input", "Process", "Output");
  const n = labels.length;
  const u = Math.min(box.h / 38, box.w / 34);

  // world → screen (isometric). Stages march along +X; a little Z depth per box.
  const s = u * 1.0;
  const proj = (x: number, y: number, z: number): P2 => ({
    x: (x - z) * COS * s,
    y: (x + z) * SIN * s - y * s,
  });

  const cw = 7, ch = 5, cd = 7;              // cuboid size in world units
  const gap = 6;                              // spacing between stages
  const stages = labels.map((l, i) => ({ l, x: i * (cw + gap) }));

  // fit: project all corners, centre + scale into the box
  const pts: P2[] = [];
  stages.forEach((st) => {
    for (const dx of [0, cw]) for (const dy of [0, ch]) for (const dz of [0, cd]) pts.push(proj(st.x + dx, dy, dz));
  });
  const minX = Math.min(...pts.map((p) => p.x)), maxX = Math.max(...pts.map((p) => p.x));
  const minY = Math.min(...pts.map((p) => p.y)), maxY = Math.max(...pts.map((p) => p.y));
  const fit = Math.min((box.w * 0.9) / (maxX - minX || 1), (box.h * 0.6) / (maxY - minY || 1));
  const ox = box.cx - ((minX + maxX) / 2) * fit;
  const oy = box.cy - ((minY + maxY) / 2) * fit - box.h * 0.04;
  const S = (p: P2): P2 => ({ x: ox + p.x * fit, y: oy + p.y * fit });

  const cuboid = (wx: number, active: number, fill: string) => {
    const c = (dx: number, dy: number, dz: number) => S(proj(wx + dx, dy, dz));
    const top = [c(0, ch, 0), c(cw, ch, 0), c(cw, ch, cd), c(0, ch, cd)];
    const right = [c(cw, 0, 0), c(cw, ch, 0), c(cw, ch, cd), c(cw, 0, cd)];
    const front = [c(0, 0, cd), c(cw, 0, cd), c(cw, ch, cd), c(0, ch, cd)];
    const poly = (pp: P2[]) => pp.map((p) => `${p.x},${p.y}`).join(" ");
    const lift = active * u * 1.2;
    return { top, right, front, poly, lift, fill };
  };

  // a packet glides from each stage to the next along the top edge
  const period = 1.6;
  return (
    <g>
      {/* ground shadow ellipses */}
      {stages.map((st, i) => {
        const g = S(proj(st.x + cw / 2, 0, cd / 2));
        return <ellipse key={`sh${i}`} cx={g.x} cy={g.y + u * 1.2} rx={cw * fit * s * 0.6} ry={cw * fit * s * 0.24}
          fill="#000" opacity={theme.dark ? 0.28 : 0.12} />;
      })}
      {/* connectors: flowing packet + link between adjacent stages */}
      {stages.slice(0, -1).map((st, i) => {
        const a = S(proj(st.x + cw, ch * 0.5, cd / 2));
        const b = S(proj(stages[i + 1].x, ch * 0.5, cd / 2));
        const f = ((t / period + i * 0.33) % 1);
        const px = a.x + (b.x - a.x) * f, py = a.y + (b.y - a.y) * f;
        return (
          <g key={`c${i}`}>
            <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={P.primary} strokeWidth={u * 0.35} strokeOpacity={0.4} strokeDasharray={`${u * 0.6} ${u * 0.6}`} />
            <g transform={`translate(${px} ${py})`}>
              <rect x={-u * 0.7} y={-u * 0.7} width={u * 1.4} height={u * 1.4} rx={u * 0.3} fill={P.highlight}
                transform="rotate(45)" opacity={0.85} />
            </g>
          </g>
        );
      })}
      {/* the 3D boxes (back-to-front) */}
      {stages.map((st, i) => {
        const wave = clamp01(1 - Math.abs(((t / period) % n) - i) * 1.3); // pulse travels the pipeline
        const base = i === n - 1 ? P.positive : i === 0 ? P.secondary : P.primary;
        const cb = cuboid(st.x, wave, base);
        const icon = iconForLabel(st.l);
        const topC = S(proj(st.x + cw / 2, ch, cd / 2));
        const frontC = S(proj(st.x + cw / 2, ch * 0.5, cd));
        return (
          <g key={`b${i}`} transform={`translate(0 ${-cb.lift})`}>
            <polygon points={cb.poly(cb.right)} fill={shade(base, 0.62)} stroke={shade(base, 0.5)} strokeWidth={1} />
            <polygon points={cb.poly(cb.front)} fill={shade(base, 0.82)} stroke={shade(base, 0.6)} strokeWidth={1} />
            <polygon points={cb.poly(cb.top)} fill={shade(base, 1.12)} stroke={shade(base, 0.7)} strokeWidth={1} opacity={0.95 + 0.05 * wave} />
            {icon && <Icon k={icon} cx={topC.x} cy={topC.y} size={cw * fit * s * 0.7} color={theme.dark ? "#0b0d10" : "#ffffff"} opacity={0.85} />}
            <text x={frontC.x} y={frontC.y + u * 3.6} textAnchor="middle" fontFamily={theme.body.family}
              fontWeight={800} fontSize={u * 1.9} fill={P.ink}>{st.l.length > 14 ? st.l.slice(0, 13) + "…" : st.l}</text>
          </g>
        );
      })}
    </g>
  );
};
